import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Radio,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";
import { reassignSubmissionCoachApi } from "src/DAL/tracker/trackerSubmission";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const INK = "#0f172a";
const PRIMARY = "var(--portal-theme-primary, #04abbb)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary, #04abbb) 14%, white)";

const coachIdOf = (coach) => String(coach?.id || coach?._id || "");
const coachNameOf = (coach) =>
  `${coach?.first_name || ""} ${coach?.last_name || ""}`.trim() ||
  coach?.name ||
  coach?.email ||
  "Coach";

const GroupLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.45,
      color: MUTED,
      textTransform: "uppercase",
      mt: 0.5,
    }}
  >
    {children}
  </Typography>
);

/**
 * Admin: move this person to another coach selected on this tracker (only
 * those are offered). Check-ins not reviewed yet and all new ones follow the
 * new coach; reviewed ones stay with the coach who reviewed them.
 */
export default function ReassignCoachDialog({
  open,
  onClose,
  coaches = [],
  currentCoachId = "",
  submissionId,
  memberName = "",
  onReassigned,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelected(String(currentCoachId || ""));
    setSearch("");
  }, [open, currentCoachId]);

  // Only the coaches selected on this tracker.
  const trackerCoaches = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return coaches.filter(
      (coach) =>
        coachIdOf(coach) &&
        (!needle ||
          `${coachNameOf(coach)} ${coach?.email || ""}`
            .toLowerCase()
            .includes(needle))
    );
  }, [coaches, search]);

  const unchanged = !selected || selected === String(currentCoachId || "");

  const handleSave = async () => {
    if (unchanged || !submissionId) return;
    setSaving(true);
    try {
      const res = await reassignSubmissionCoachApi(submissionId, selected);
      if (res?.code !== 200) {
        enqueueSnackbar(res?.message || "Could not reassign the coach", {
          variant: "error",
        });
        return;
      }
      enqueueSnackbar(res.message || "Coach reassigned", { variant: "success" });
      onReassigned?.(res.submission, {
        coach: res.coach,
        addedToJourney: Boolean(res.addedToJourney),
      });
      onClose?.();
    } catch (_err) {
      enqueueSnackbar("Could not reassign the coach", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const renderCoach = (coach) => {
    const id = coachIdOf(coach);
    const name = coachNameOf(coach);
    const checked = selected === id;
    const isCurrent = id === String(currentCoachId || "");
    return (
      <Box
        key={id}
        component="label"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 0.75,
          borderRadius: "10px",
          border: `1px solid ${checked ? PRIMARY : BORDER}`,
          bgcolor: checked ? PRIMARY_SOFT : "#fff",
          cursor: "pointer",
        }}
      >
        <Radio
          size="small"
          checked={checked}
          onChange={() => setSelected(id)}
          sx={{ p: 0.5, "&.Mui-checked": { color: PRIMARY } }}
        />
        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: 12,
            fontWeight: 700,
            bgcolor: PRIMARY_SOFT,
            color: PRIMARY,
          }}
        >
          {name.slice(0, 1).toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }}>
            {name}
            {isCurrent ? (
              <Box
                component="span"
                sx={{ ml: 0.75, fontSize: 11.5, fontWeight: 600, color: MUTED }}
              >
                (current)
              </Box>
            ) : null}
          </Typography>
          {coach?.email ? (
            <Typography
              sx={{
                fontSize: 12,
                color: MUTED,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {coach.email}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 18, color: INK }}>
          Reassign coach
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 13, color: MUTED }}>
          {memberName ? `${memberName}'s` : "This person's"} check-ins that
          aren't reviewed yet, and all new ones, move to the coach you pick.
          Reviewed check-ins stay with the coach who reviewed them.
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          disabled={saving}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ display: "grid", gap: 0.75, maxHeight: { xs: "60vh", sm: 420 } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: "10px",
            border: `1px solid ${BORDER}`,
            "&:focus-within": { borderColor: PRIMARY },
          }}
        >
          <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
          <InputBase
            fullWidth
            placeholder="Search coaches"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ fontSize: 13 }}
          />
        </Box>

        <GroupLabel>Coaches on this tracker</GroupLabel>
        {trackerCoaches.length ? (
          trackerCoaches.map(renderCoach)
        ) : (
          <Typography sx={{ fontSize: 12.5, color: MUTED, py: 0.5 }}>
            {search
              ? "No coaches match your search."
              : "No coaches are selected on this tracker. Add them in the tracker's coach settings."}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.75, gap: 1 }}>
        <button
          type="button"
          className="small-contained-outlined-button"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="small-contained-button"
          onClick={handleSave}
          disabled={saving || unchanged}
          style={{ opacity: saving || unchanged ? 0.6 : 1 }}
        >
          {saving ? "Saving…" : "Reassign"}
        </button>
      </DialogActions>
    </Dialog>
  );
}
